import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ArrowLeft, Save, Search, Building2, X, Loader2, Check, FileText, Truck, Info, ScanLine, Container } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TransporteurCombobox = ({ value, label, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const fetchPartners = useCallback(async (q) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (q && q.length >= 2) params.append('search', q);
      const res = await axios.get(`${API}/transport-partners/search-for-assignment?${params}`, {});
      setResults(res.data.partners || []);
    } catch { setResults([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (open) fetchPartners('');
  }, [open, fetchPartners]);

  const onSearch = (q) => {
    setSearch(q);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPartners(q), 300);
  };

  const select = (partnerId, partnerName) => {
    onChange(partnerId, partnerName);
    setOpen(false);
    setSearch('');
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange(null, '');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between h-10 font-normal text-left"
          data-testid="transporteur-combobox"
        >
          {value ? (
            <span className="flex items-center gap-2 truncate">
              <Building2 className="w-4 h-4 text-emerald-600 shrink-0" />
              <span className="truncate">{label}</span>
            </span>
          ) : (
            <span className="text-muted-foreground">Selectionner un transporteur...</span>
          )}
          {value ? (
            <X className="w-4 h-4 text-gray-400 hover:text-red-500 shrink-0" onClick={clear} />
          ) : (
            <Search className="w-4 h-4 text-gray-400 shrink-0" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <div className="p-2 border-b">
          <Input
            placeholder="Rechercher par nom, téléphone…"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            className="h-8 text-sm"
            autoFocus
            data-testid="transporteur-search"
          />
        </div>
        <div className="max-h-[280px] overflow-y-auto">
          <button
            onClick={() => select('__baobab__', 'Baobab Connect')}
            className="w-full flex items-center gap-3 px-3 py-2 hover:bg-emerald-50 transition-colors text-left border-b"
            data-testid="transporteur-option-baobab"
          >
            <div className="w-7 h-7 rounded-md bg-emerald-600 flex items-center justify-center shrink-0">
              <Building2 className="w-3.5 h-3.5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-800">Baobab Connect</p>
              <p className="text-[10px] text-gray-400">Flotte interne</p>
            </div>
            {value === '__baobab__' && <Check className="w-4 h-4 text-emerald-600 shrink-0" />}
          </button>
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
          ) : results.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">Aucun transporteur trouve</p>
          ) : (
            results.map(p => (
              <button
                key={p.id}
                onClick={() => select(p.id, p.name)}
                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-emerald-50 transition-colors text-left"
                data-testid={`transporteur-option-${p.id}`}
              >
                <div className={`w-7 h-7 rounded-md flex items-center justify-center text-[10px] font-bold shrink-0 ${p.type === 'Independant' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>
                  {(p.name || '?').substring(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{p.name}</p>
                  <p className="text-[10px] text-gray-400">
                    {p.type === 'Independant' ? 'Independant' : 'Partenaire'}
                    {p.phone ? ` · ${p.phone}` : ''}
                  </p>
                </div>
                {value === p.id && <Check className="w-4 h-4 text-emerald-600 shrink-0" />}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const GENRE_OPTIONS = [
  'Camion', 'Tracteur Routier', 'Semi Remorque', 'Remorque',
  'Fourgon', 'Citerne', 'Benne', 'Porte-Conteneur', 'Plateau', 'Autre'
];

const ENERGY_OPTIONS = ['Gas-Oil', 'Diesel', 'Essence', 'Electrique', 'Sans Energie'];
const USAGE_OPTIONS = ['Public', 'Prive'];

const VehicleForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    registration: '', vehicleType: 'plateau', capacity: '', brand: '', model: '',
    year: '', owner: '', internalComment: '', partnerId: null, partnerName: '',
    // Carte Grise Tracteur
    carteGriseNumber: '', genre: '', commercialType: '', color: '', bodyType: '',
    firstRegistrationDate: '', carteGriseDate: '', energy: '', seats: '',
    ptacKg: '', pvKg: '', cuKg: '', fiscalPower: '', engineCapacity: '',
    axles: '', ownerIdentity: '', usage: '',
    // Verso Tracteur
    vinChassis: '', engineNumber: '', technicalType: '', previousRegistration: '', creditCompany: '',
    // Semi-Remorque
    semi_registration: '', semi_carteGriseNumber: '', semi_brand: '', semi_commercialType: '',
    semi_genre: 'Semi Remorque', semi_color: '', semi_bodyType: '', semi_energy: 'Sans Energie',
    semi_firstRegistrationDate: '', semi_carteGriseDate: '', semi_ownerIdentity: '', semi_usage: '',
    semi_ptacKg: '', semi_pvKg: '', semi_cuKg: '', semi_fiscalPower: '', semi_engineCapacity: '',
    semi_axles: '', semi_seats: '',
    semi_vinChassis: '', semi_engineNumber: '', semi_technicalType: '', semi_previousRegistration: '', semi_creditCompany: ''
  });

  useEffect(() => { if (isEdit) fetchVehicle(); }, [id]);

  const fetchVehicle = async () => {
    try {
      const res = await axios.get(`${API}/vehicles/${id}`);
      const v = res.data;
      const st = v.semiTrailer || {};
      setFormData({
        registration: v.registration || '', vehicleType: v.vehicleType || 'plateau',
        capacity: v.capacity ? String(v.capacity) : '', brand: v.brand || '',
        model: v.model || '', year: v.year ? String(v.year) : '', owner: v.owner || '',
        internalComment: v.internalComment || '', partnerId: v.partnerId || null,
        partnerName: v.partnerName || '',
        carteGriseNumber: v.carteGriseNumber || '', genre: v.genre || '',
        commercialType: v.commercialType || '', color: v.color || '',
        bodyType: v.bodyType || '', firstRegistrationDate: v.firstRegistrationDate || '',
        carteGriseDate: v.carteGriseDate || '', energy: v.energy || '',
        seats: v.seats ? String(v.seats) : '', ptacKg: v.ptacKg ? String(v.ptacKg) : '',
        pvKg: v.pvKg ? String(v.pvKg) : '', cuKg: v.cuKg ? String(v.cuKg) : '',
        fiscalPower: v.fiscalPower ? String(v.fiscalPower) : '',
        engineCapacity: v.engineCapacity ? String(v.engineCapacity) : '',
        axles: v.axles ? String(v.axles) : '', ownerIdentity: v.ownerIdentity || '',
        usage: v.usage || '',
        vinChassis: v.vinChassis || '', engineNumber: v.engineNumber || '',
        technicalType: v.technicalType || '', previousRegistration: v.previousRegistration || '',
        creditCompany: v.creditCompany || '',
        // Semi-Remorque
        semi_registration: st.registration || '', semi_carteGriseNumber: st.carteGriseNumber || '',
        semi_brand: st.brand || '', semi_commercialType: st.commercialType || '',
        semi_genre: st.genre || 'Semi Remorque', semi_color: st.color || '',
        semi_bodyType: st.bodyType || '', semi_energy: st.energy || 'Sans Energie',
        semi_firstRegistrationDate: st.firstRegistrationDate || '', semi_carteGriseDate: st.carteGriseDate || '',
        semi_ownerIdentity: st.ownerIdentity || '', semi_usage: st.usage || '',
        semi_ptacKg: st.ptacKg ? String(st.ptacKg) : '', semi_pvKg: st.pvKg ? String(st.pvKg) : '',
        semi_cuKg: st.cuKg ? String(st.cuKg) : '', semi_fiscalPower: st.fiscalPower ? String(st.fiscalPower) : '',
        semi_engineCapacity: st.engineCapacity ? String(st.engineCapacity) : '',
        semi_axles: st.axles ? String(st.axles) : '', semi_seats: st.seats ? String(st.seats) : '',
        semi_vinChassis: st.vinChassis || '', semi_engineNumber: st.engineNumber || '',
        semi_technicalType: st.technicalType || '', semi_previousRegistration: st.previousRegistration || '',
        semi_creditCompany: st.creditCompany || ''
      });
    } catch {
      toast({ title: "Erreur", description: "Impossible de charger le vehicule", variant: "destructive" });
    }
  };

  const handleTransporteurChange = (partnerId, partnerName) => {
    if (!partnerId) {
      setFormData(prev => ({ ...prev, partnerId: null, partnerName: '', owner: '' }));
    } else if (partnerId === '__baobab__') {
      setFormData(prev => ({ ...prev, partnerId: null, partnerName: '', owner: 'Baobab Connect' }));
    } else {
      setFormData(prev => ({ ...prev, partnerId, partnerName, owner: partnerName }));
    }
  };

  const set = (field) => (e) => setFormData(prev => ({ ...prev, [field]: e.target.value }));
  const setSelect = (field) => (val) => setFormData(prev => ({ ...prev, [field]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.registration || !formData.capacity) {
      toast({ title: "Erreur", description: "Immatriculation et capacite sont obligatoires", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      // Build semiTrailer object if any semi field is filled
      const semiFields = Object.keys(formData).filter(k => k.startsWith('semi_'));
      const hasSemiData = semiFields.some(k => formData[k] && formData[k] !== 'Semi Remorque' && formData[k] !== 'Sans Energie');
      
      let semiTrailer = null;
      if (hasSemiData) {
        semiTrailer = {};
        const mapping = {
          semi_registration: 'registration', semi_carteGriseNumber: 'carteGriseNumber',
          semi_brand: 'brand', semi_commercialType: 'commercialType', semi_genre: 'genre',
          semi_color: 'color', semi_bodyType: 'bodyType', semi_energy: 'energy',
          semi_firstRegistrationDate: 'firstRegistrationDate', semi_carteGriseDate: 'carteGriseDate',
          semi_ownerIdentity: 'ownerIdentity', semi_usage: 'usage',
          semi_vinChassis: 'vinChassis', semi_engineNumber: 'engineNumber',
          semi_technicalType: 'technicalType', semi_previousRegistration: 'previousRegistration',
          semi_creditCompany: 'creditCompany'
        };
        const numMapping = {
          semi_ptacKg: ['ptacKg', parseFloat], semi_pvKg: ['pvKg', parseFloat],
          semi_cuKg: ['cuKg', parseFloat], semi_fiscalPower: ['fiscalPower', parseInt],
          semi_engineCapacity: ['engineCapacity', parseInt], semi_axles: ['axles', parseInt],
          semi_seats: ['seats', parseInt]
        };
        for (const [src, dst] of Object.entries(mapping)) {
          if (formData[src]) semiTrailer[dst] = formData[src];
        }
        for (const [src, [dst, parser]] of Object.entries(numMapping)) {
          if (formData[src]) semiTrailer[dst] = parser(formData[src]);
        }
      }

      const payload = {
        registration: formData.registration,
        vehicleType: formData.vehicleType,
        capacity: parseFloat(formData.capacity),
        brand: formData.brand || undefined,
        model: formData.model || undefined,
        year: formData.year ? parseInt(formData.year) : undefined,
        owner: formData.owner || undefined,
        internalComment: formData.internalComment || undefined,
        // Carte Grise Tracteur
        carteGriseNumber: formData.carteGriseNumber || undefined,
        genre: formData.genre || undefined,
        commercialType: formData.commercialType || undefined,
        color: formData.color || undefined,
        bodyType: formData.bodyType || undefined,
        firstRegistrationDate: formData.firstRegistrationDate || undefined,
        carteGriseDate: formData.carteGriseDate || undefined,
        energy: formData.energy || undefined,
        seats: formData.seats ? parseInt(formData.seats) : undefined,
        ptacKg: formData.ptacKg ? parseFloat(formData.ptacKg) : undefined,
        pvKg: formData.pvKg ? parseFloat(formData.pvKg) : undefined,
        cuKg: formData.cuKg ? parseFloat(formData.cuKg) : undefined,
        fiscalPower: formData.fiscalPower ? parseInt(formData.fiscalPower) : undefined,
        engineCapacity: formData.engineCapacity ? parseInt(formData.engineCapacity) : undefined,
        axles: formData.axles ? parseInt(formData.axles) : undefined,
        ownerIdentity: formData.ownerIdentity || undefined,
        usage: formData.usage || undefined,
        vinChassis: formData.vinChassis || undefined,
        engineNumber: formData.engineNumber || undefined,
        technicalType: formData.technicalType || undefined,
        previousRegistration: formData.previousRegistration || undefined,
        creditCompany: formData.creditCompany || undefined,
        semiTrailer: semiTrailer,
      };

      if (formData.partnerId) {
        payload.partnerId = formData.partnerId;
        payload.partnerName = formData.partnerName;
      } else if (isEdit) {
        payload.partnerId = '';
        payload.partnerName = '';
      }

      if (isEdit) {
        await axios.put(`${API}/vehicles/${id}`, payload);
        toast({ title: "Véhicule modifié" });
      } else {
        const res = await axios.post(`${API}/vehicles`, payload);
        const vehicleId = res.data?.id;
        toast({ title: "Véhicule créé" });
        // Upload pending OCR files after creation
        if (vehicleId && pendingFiles.length > 0) {
          for (const pf of pendingFiles) {
            try {
              const fd = new FormData();
              fd.append('file', pf.file); fd.append('type', pf.type);
              fd.append('side', pf.side.replace('semi_', '')); fd.append('entityType', 'vehicle');
              fd.append('entityId', vehicleId); fd.append('extract', 'false');
              await axios.post(`${API}/ocr/documents/upload`, fd, {
                headers: { Authorization: 'Bearer cookie-auth', 'Content-Type': 'multipart/form-data' }
              });
            } catch { /* ignore individual upload errors */ }
          }
        }
      }
      navigate('/admin/vehicles');
    } catch (error) {
      toast({ title: "Erreur", description: error.response?.data?.detail || "Impossible de sauvegarder", variant: "destructive" });
    } finally { setLoading(false); }
  };

  const [ocrLoading, setOcrLoading] = useState({ recto: false, verso: false, semi_recto: false, semi_verso: false });
  const [ocrImages, setOcrImages] = useState({ recto: null, verso: null, semi_recto: null, semi_verso: null });
  const [ocrExtracted, setOcrExtracted] = useState({ recto: null, verso: null, semi_recto: null, semi_verso: null });
  const [pendingFiles, setPendingFiles] = useState([]);

  const applyExtractedToForm = (extracted, side) => {
    if (!extracted) return;
    const isSemi = side.startsWith('semi_');
    const prefix = isSemi ? 'semi_' : '';
    
    setFormData(prev => {
      const updated = { ...prev };
      Object.entries(extracted).forEach(([key, val]) => {
        if (val !== null && val !== undefined) {
          const formKey = prefix + key;
          if (formKey in updated) {
            updated[formKey] = typeof val === 'number' ? String(val) : val;
          }
        }
      });
      
      if (!isSemi) {
        // Auto-derive capacity from CU (Kg)
        if (extracted.cuKg && !updated.capacity) {
          const cuVal = parseFloat(extracted.cuKg);
          if (!isNaN(cuVal) && cuVal > 0) updated.capacity = String(Math.round(cuVal / 1000 * 10) / 10);
        }
        // Auto-derive year from firstRegistrationDate
        if (extracted.firstRegistrationDate && !updated.year) {
          const year = extracted.firstRegistrationDate.split('-')[0];
          if (year && year.length === 4) updated.year = year;
        }
        if (extracted.brand && !updated.brand) updated.brand = extracted.brand;
        if (extracted.genre && side === 'recto') {
          const genreMap = {
            'Semi Remorque': 'plateau', 'Tracteur Routier': 'autre',
            'Camion': 'autre', 'Citerne': 'citerne', 'Benne': 'benne',
            'Fourgon': 'fourgon', 'Porte-Conteneur': 'porte_conteneur', 'Plateau': 'plateau'
          };
          const mapped = genreMap[extracted.genre];
          if (mapped) updated.vehicleType = mapped;
        }
      }
      return updated;
    });
  };

  const handleOcrUpload = async (side, file) => {
    if (!file) return;
    setOcrLoading(prev => ({ ...prev, [side]: true }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('type', 'carte_grise');
      // Backend expects "recto" or "verso" only — strip semi_ prefix
      const apiSide = side.replace('semi_', '');
      fd.append('side', apiSide);

      let res;
      if (isEdit) {
        // Edit mode: upload + extract directly
        fd.append('entityType', 'vehicle');
        fd.append('entityId', id);
        fd.append('extract', 'true');
        res = await axios.post(`${API}/ocr/documents/upload`, fd, {
          headers: { Authorization: 'Bearer cookie-auth', 'Content-Type': 'multipart/form-data' }
        });
      } else {
        // Creation mode: extract only, save file for later upload
        res = await axios.post(`${API}/ocr/extract-only`, fd, {
          headers: { Authorization: 'Bearer cookie-auth', 'Content-Type': 'multipart/form-data' }
        });
        setPendingFiles(prev => [...prev.filter(p => p.side !== side), { side, file, type: 'carte_grise' }]);
      }

      const doc = res.data;
      if (isEdit) setOcrImages(prev => ({ ...prev, [side]: doc.fileUrl }));

      if (doc.extractionStatus === 'success' && doc.extractedData) {
        setOcrExtracted(prev => ({ ...prev, [side]: doc.extractedData }));
        applyExtractedToForm(doc.extractedData, side);
        toast({ title: `Extraction ${side} reussie`, description: `${Object.keys(doc.extractedData).filter(k => doc.extractedData[k]).length} champs extraits` });
      } else if (doc.extractedData?.error) {
        toast({ title: 'Extraction échouée', description: doc.extractedData.error, variant: 'destructive' });
      } else {
        toast({ title: isEdit ? 'Image uploadee' : 'Extraction échouée', description: 'Aucun champ extrait - remplissez manuellement' });
      }
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Erreur upload/extraction', variant: 'destructive' });
    }
    setOcrLoading(prev => ({ ...prev, [side]: false }));
  };

  const comboValue = formData.partnerId || (formData.owner === 'Baobab Connect' ? '__baobab__' : null);
  const comboLabel = formData.partnerName || formData.owner || '';

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={() => navigate('/admin/vehicles')} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">
            {isEdit ? 'Modifier le vehicule' : 'Nouveau vehicule'}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {isEdit ? 'Mettre a jour les informations' : 'Ajouter un vehicule au parc'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Section 1: Identification */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-base">
              <Truck className="w-4 h-4 text-emerald-600" />
              Identification du véhicule
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="registration" className="text-xs font-medium">Immatriculation *</Label>
                <Input id="registration" value={formData.registration} onChange={set('registration')}
                  placeholder="AA-1234-BB" required className="uppercase" data-testid="vehicle-registration" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Type de véhicule *</Label>
                <Select value={formData.vehicleType} onValueChange={setSelect('vehicleType')}>
                  <SelectTrigger data-testid="vehicle-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="plateau">Plateau</SelectItem>
                    <SelectItem value="fourgon">Fourgon</SelectItem>
                    <SelectItem value="fourgon_frigo">Fourgon Frigorifique</SelectItem>
                    <SelectItem value="citerne">Citerne</SelectItem>
                    <SelectItem value="benne">Benne</SelectItem>
                    <SelectItem value="porte_conteneur">Porte-conteneur</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Capacite (tonnes) *</Label>
                <Input type="number" step="0.1" min="0" value={formData.capacity}
                  onChange={set('capacity')} required data-testid="vehicle-capacity" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Marque</Label>
                <Input value={formData.brand} onChange={set('brand')} placeholder="DAF, Mercedes, Volvo..." data-testid="vehicle-brand" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Modele</Label>
                <Input value={formData.model} onChange={set('model')} placeholder="Actros, FH16..." data-testid="vehicle-model" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Couleur</Label>
                <Input value={formData.color} onChange={set('color')} placeholder="BLANC, ROUGE..." data-testid="vehicle-color" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Transporteur</Label>
                <TransporteurCombobox value={comboValue} label={comboLabel} onChange={handleTransporteurChange} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Commentaire interne</Label>
                <Input value={formData.internalComment} onChange={set('internalComment')} placeholder="Notes internes..." data-testid="vehicle-comment" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Carte Grise - Tabs Tracteur / Semi-Remorque */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="w-4 h-4 text-amber-600" />
              Informations Carte Grise
            </CardTitle>
            <p className="text-xs text-gray-400 mt-0.5">Un camion a 2 cartes grises : une pour le tracteur et une pour la semi-remorque</p>
          </CardHeader>
          <CardContent className="pt-2">
            <Tabs defaultValue="tracteur" className="w-full">
              <TabsList className="w-full grid grid-cols-2 mb-4" data-testid="cg-tabs">
                <TabsTrigger value="tracteur" className="flex items-center gap-1.5 text-xs" data-testid="cg-tab-tracteur">
                  <Truck className="w-3.5 h-3.5" /> Tracteur
                  {formData.carteGriseNumber && <Badge variant="secondary" className="text-[9px] ml-1 bg-emerald-100 text-emerald-700 px-1">CG</Badge>}
                </TabsTrigger>
                <TabsTrigger value="semi" className="flex items-center gap-1.5 text-xs" data-testid="cg-tab-semi">
                  <Container className="w-3.5 h-3.5" /> Semi-Remorque
                  {formData.semi_carteGriseNumber && <Badge variant="secondary" className="text-[9px] ml-1 bg-blue-100 text-blue-700 px-1">CG</Badge>}
                </TabsTrigger>
              </TabsList>

              {/* ─── TRACTEUR ─── */}
              <TabsContent value="tracteur" className="space-y-4">
                <CarteGriseOcrZone
                  sides={['recto', 'verso']}
                  ocrLoading={ocrLoading}
                  ocrImages={ocrImages}
                  ocrExtracted={ocrExtracted}
                  handleOcrUpload={handleOcrUpload}
                  label="Tracteur"
                />
                {(ocrExtracted.recto || ocrExtracted.verso) && (
                  <OcrSummary extracted={{ ...ocrExtracted.recto, ...ocrExtracted.verso }} />
                )}
                <CarteGriseFields formData={formData} set={set} setFormData={setFormData} prefix="" />
              </TabsContent>

              {/* ─── SEMI-REMORQUE ─── */}
              <TabsContent value="semi" className="space-y-4">
                <CarteGriseOcrZone
                  sides={['semi_recto', 'semi_verso']}
                  ocrLoading={ocrLoading}
                  ocrImages={ocrImages}
                  ocrExtracted={ocrExtracted}
                  handleOcrUpload={handleOcrUpload}
                  label="Semi-Remorque"
                />
                {(ocrExtracted.semi_recto || ocrExtracted.semi_verso) && (
                  <OcrSummary extracted={{ ...ocrExtracted.semi_recto, ...ocrExtracted.semi_verso }} />
                )}
                <CarteGriseFields formData={formData} set={set} setFormData={setFormData} prefix="semi_" />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate('/admin/vehicles')}>Annuler</Button>
          <Button type="submit" disabled={loading} className="bg-[#1db584] hover:bg-[#16a075]" data-testid="vehicle-submit">
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Enregistrement...' : (isEdit ? 'Mettre a jour' : 'Creer le vehicule')}
          </Button>
        </div>
      </form>
    </div>
  );
};

// ─── Composant OCR Upload Zone ────────────────────────────────────
const CarteGriseOcrZone = ({ sides, ocrLoading, ocrImages, ocrExtracted, handleOcrUpload, label }) => (
  <div className="flex gap-3">
    {sides.map(side => {
      const displayName = side.includes('recto') ? 'recto' : 'verso';
      return (
        <label
          key={side}
          className={`flex-1 relative border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-colors
            ${ocrLoading[side] ? 'border-amber-300 bg-amber-50' : (ocrImages[side] || ocrExtracted[side]) ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200 hover:border-amber-300 hover:bg-amber-50/50'}`}
          data-testid={`ocr-upload-${side}`}
        >
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => handleOcrUpload(side, e.target.files?.[0])}
            disabled={ocrLoading[side]}
          />
          {ocrLoading[side] ? (
            <div className="flex flex-col items-center gap-1">
              <Loader2 className="w-5 h-5 animate-spin text-amber-500" />
              <span className="text-[10px] text-amber-600 font-medium">Extraction {displayName}...</span>
            </div>
          ) : (ocrImages[side] || ocrExtracted[side]) ? (
            <div className="flex flex-col items-center gap-1">
              <Check className="w-5 h-5 text-emerald-500" />
              <span className="text-[10px] text-emerald-600 font-medium">{label} {displayName} scanne</span>
              {ocrExtracted[side] && <Badge variant="secondary" className="text-[9px] bg-emerald-100 text-emerald-700">OCR OK</Badge>}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <ScanLine className="w-5 h-5 text-gray-400" />
              <span className="text-[10px] text-gray-500 font-medium">Scanner {displayName}</span>
            </div>
          )}
        </label>
      );
    })}
  </div>
);

// ─── OCR Extracted Summary ────────────────────────────────────────
const OcrSummary = ({ extracted }) => {
  const entries = Object.entries(extracted || {}).filter(([_, v]) => v != null && v !== '' && v !== 0);
  if (!entries.length) return null;
  return (
    <div className="p-3 rounded-lg border border-emerald-200 bg-emerald-50/50" data-testid="ocr-extracted-summary">
      <p className="text-xs font-semibold text-emerald-700 mb-2">Données extraites</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
        {entries.map(([key, val]) => (
          <div key={key} className="text-[11px]">
            <span className="text-gray-400">{CG_FIELD_LABELS[key] || key}:</span>{' '}
            <span className="font-medium text-gray-800">{String(val)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Composant Carte Grise Fields (reutilise pour Tracteur et Semi) ──
const CarteGriseFields = ({ formData, set, setFormData, prefix }) => {
  const f = (field) => prefix + field;
  const val = (field) => formData[f(field)] || '';
  const setField = (field) => set(f(field));
  const setSelectField = (field) => (v) => setFormData(prev => ({ ...prev, [f(field)]: v === '_none' ? '' : v }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs font-medium">Immatriculation {prefix ? 'Semi' : ''}</Label>
          <Input value={val('registration')} onChange={setField('registration')} placeholder="AA-1234-BB" className="uppercase"
            data-testid={`${prefix}cg-registration`} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium">N Carte Grise</Label>
          <Input value={val('carteGriseNumber')} onChange={setField('carteGriseNumber')} placeholder="CG62001684"
            data-testid={`${prefix}cg-number`} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium">Date edition CG</Label>
          <Input type="date" value={val('carteGriseDate')} onChange={setField('carteGriseDate')}
            data-testid={`${prefix}cg-date`} />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs font-medium">Date 1ere mise en circulation</Label>
          <Input type="date" value={val('firstRegistrationDate')} onChange={setField('firstRegistrationDate')}
            data-testid={`${prefix}cg-first-reg`} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium">Genre</Label>
          <Select value={val('genre') || '_none'} onValueChange={setSelectField('genre')}>
            <SelectTrigger data-testid={`${prefix}cg-genre`}><SelectValue placeholder="Selectionner..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">-- Non renseigne --</SelectItem>
              {GENRE_OPTIONS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium">{prefix ? 'Marque' : 'Type commercial'}</Label>
          {prefix ? (
            <Input value={val('brand')} onChange={setField('brand')} placeholder="TROUILLET, FRUEHAUF..."
              data-testid={`${prefix}cg-brand`} />
          ) : (
            <Input value={val('commercialType')} onChange={setField('commercialType')} placeholder="95XF 430, 3139 D..."
              data-testid={`${prefix}cg-commercial-type`} />
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {prefix && (
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Type commercial</Label>
            <Input value={val('commercialType')} onChange={setField('commercialType')} placeholder="NA-TRO, SN-348..."
              data-testid={`${prefix}cg-commercial-type`} />
          </div>
        )}
        <div className="space-y-1.5">
          <Label className="text-xs font-medium">Carrosserie</Label>
          <Input value={val('bodyType')} onChange={setField('bodyType')} placeholder={prefix ? 'Plateau, Citerne, Benne...' : 'Porte-Cont, Pour Semi-R...'}
            data-testid={`${prefix}cg-body-type`} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium">Couleur</Label>
          <Input value={val('color')} onChange={setField('color')} placeholder="BLANC, GRIS..."
            data-testid={`${prefix}cg-color`} />
        </div>
        {!prefix && (
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Energie</Label>
            <Select value={val('energy') || '_none'} onValueChange={setSelectField('energy')}>
              <SelectTrigger data-testid={`${prefix}cg-energy`}><SelectValue placeholder="Selectionner..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">-- Non renseigne --</SelectItem>
                {ENERGY_OPTIONS.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {prefix && (
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Energie</Label>
            <Select value={val('energy') || '_none'} onValueChange={setSelectField('energy')}>
              <SelectTrigger data-testid={`${prefix}cg-energy`}><SelectValue placeholder="Selectionner..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">-- Non renseigne --</SelectItem>
                {ENERGY_OPTIONS.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="space-y-1.5">
          <Label className="text-xs font-medium">Usage</Label>
          <Select value={val('usage') || '_none'} onValueChange={setSelectField('usage')}>
            <SelectTrigger data-testid={`${prefix}cg-usage`}><SelectValue placeholder="Selectionner..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">-- Non renseigne --</SelectItem>
              {USAGE_OPTIONS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium">Places assises</Label>
          <Input type="number" min="0" value={val('seats')} onChange={setField('seats')} placeholder={prefix ? '0' : '2'}
            data-testid={`${prefix}cg-seats`} />
        </div>
      </div>

      {/* Poids */}
      <div className={`rounded-lg border p-4 ${prefix ? 'border-blue-100 bg-blue-50/30' : 'border-amber-100 bg-amber-50/30'}`}>
        <p className={`text-xs font-semibold mb-3 flex items-center gap-1.5 ${prefix ? 'text-blue-700' : 'text-amber-700'}`}>
          <Info className="w-3.5 h-3.5" /> Poids et charge utile
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">PTAC (Kg)</Label>
            <Input type="number" step="0.1" min="0" value={val('ptacKg')} onChange={setField('ptacKg')}
              placeholder="32000" data-testid={`${prefix}cg-ptac`} />
            <p className="text-[10px] text-gray-400">Poids Total Autorise en Charge</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">PV (Kg)</Label>
            <Input type="number" step="0.1" min="0" value={val('pvKg')} onChange={setField('pvKg')}
              placeholder="8000" data-testid={`${prefix}cg-pv`} />
            <p className="text-[10px] text-gray-400">Poids a Vide</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">CU (Kg)</Label>
            <Input type="number" step="0.1" min="0" value={val('cuKg')} onChange={setField('cuKg')}
              placeholder="24000" className={`${prefix ? 'border-blue-300 focus:ring-blue-500' : 'border-amber-300 focus:ring-amber-500'}`}
              data-testid={`${prefix}cg-cu`} />
            <p className={`text-[10px] font-medium ${prefix ? 'text-blue-600' : 'text-amber-600'}`}>Charge Utile reelle</p>
          </div>
        </div>
      </div>

      {/* Technique */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs font-medium">Puissance fiscale (CV)</Label>
          <Input type="number" min="0" value={val('fiscalPower')} onChange={setField('fiscalPower')} placeholder="31"
            data-testid={`${prefix}cg-fiscal`} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium">Cylindree (CC)</Label>
          <Input type="number" min="0" value={val('engineCapacity')} onChange={setField('engineCapacity')} placeholder={prefix ? '0' : '11630'}
            data-testid={`${prefix}cg-engine`} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium">Nombre d'essieux</Label>
          <Input type="number" min="0" value={val('axles')} onChange={setField('axles')} placeholder={prefix ? '3' : '2'}
            data-testid={`${prefix}cg-axles`} />
        </div>
      </div>

      {/* Proprietaire */}
      <div className="space-y-1.5">
        <Label className="text-xs font-medium">Identite du proprietaire (Carte Grise)</Label>
        <Input value={val('ownerIdentity')} onChange={setField('ownerIdentity')}
          placeholder="NOM PRENOM — CNI: XXXXXXXX ou RDC: XXXXXXXX"
          data-testid={`${prefix}cg-owner-identity`} />
        <p className="text-[10px] text-gray-400">Nom complet + N CNI ou RDC tel qu'indique sur la Carte Grise</p>
      </div>

      {/* Verso */}
      <div className="rounded-lg border border-zinc-100 bg-zinc-50/50 p-4">
        <p className="text-xs font-semibold text-zinc-600 mb-3">Informations verso</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">N VIN / Chassis</Label>
            <Input value={val('vinChassis')} onChange={setField('vinChassis')} placeholder="XLRTE47XS0E492362"
              data-testid={`${prefix}cg-vin`} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Type technique</Label>
            <Input value={val('technicalType')} onChange={setField('technicalType')} placeholder="TE47XS"
              data-testid={`${prefix}cg-tech-type`} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">N Moteur</Label>
            <Input value={val('engineNumber')} onChange={setField('engineNumber')}
              data-testid={`${prefix}cg-engine-number`} />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">N Immatriculation precedent</Label>
            <Input value={val('previousRegistration')} onChange={setField('previousRegistration')}
              data-testid={`${prefix}cg-prev-reg`} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Société de credit</Label>
            <Input value={val('creditCompany')} onChange={setField('creditCompany')}
              data-testid={`${prefix}cg-credit`} />
          </div>
        </div>
      </div>
    </div>
  );
};

const CG_FIELD_LABELS = {
  registration: 'Immatriculation', carteGriseNumber: 'N CG', ownerIdentity: 'Proprietaire',
  brand: 'Marque', genre: 'Genre', commercialType: 'Type commercial', color: 'Couleur',
  bodyType: 'Carrosserie', firstRegistrationDate: 'Mise en circulation', carteGriseDate: 'Date CG',
  energy: 'Energie', seats: 'Places', ptacKg: 'PTAC (Kg)', pvKg: 'PV (Kg)', cuKg: 'CU (Kg)',
  fiscalPower: 'Puissance (CV)', engineCapacity: 'Cylindree (CC)', axles: 'Essieux', usage: 'Usage',
  vinChassis: 'VIN/Chassis', engineNumber: 'N Moteur', technicalType: 'Type technique',
  previousRegistration: 'Immat. precedente', creditCompany: 'Societe credit',
};

export default VehicleForm;
